import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Route as RouteIcon, Upload, Truck, ShieldAlert,
  Bus, LogOut, Bell, Users, KeyRound, History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import Copyright from "@/components/Copyright";
import { useLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Super-admin", owner: "Propriétaire", admin: "Admin",
  dispatch: "Répartiteur", driver: "Chauffeur",
};

const NAV = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, roles: ["super_admin", "owner", "admin", "dispatch"] },
  { to: "/routes", label: "Routes", icon: RouteIcon, roles: ["super_admin", "owner", "admin", "dispatch"] },
  { to: "/routes/import", label: "Importer", icon: Upload, roles: ["super_admin", "owner", "admin", "dispatch"] },
  { to: "/history", label: "Historique", icon: History, roles: ["super_admin", "owner", "admin", "dispatch"] },
  { to: "/fleet", label: "Flotte & Écoles", icon: Truck, roles: ["super_admin", "owner", "admin", "dispatch"] },
  { to: "/driver", label: "Vue chauffeur", icon: Bus, roles: ["super_admin", "owner", "admin", "dispatch", "driver"] },
  { to: "/admin", label: "Administration", icon: Users, roles: ["super_admin", "owner", "admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const logo = useLogo();
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Les deux mots de passe ne correspondent pas");
    if (pw.new_password.length < 8) return toast.error("Au moins 8 caractères");
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Mot de passe modifié");
      setPwOpen(false); setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col fixed h-screen z-20">
        <div className="px-6 py-6 border-b border-border">
          {logo ? (
            <div>
              <img src={logo} alt="Logo" data-testid="brand-logo"
                className="h-11 max-w-full object-contain object-left" />
              <div className="overline mt-2">School Route Optimizer</div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center" data-testid="brand-logo-fallback">
                <Bus className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <div className="font-display font-extrabold text-lg leading-none tracking-tight">Innova Route</div>
                <div className="overline mt-1">School Route Optimizer</div>
              </div>
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((n) => {
            const active = loc.pathname === n.to || (n.to !== "/dashboard" && loc.pathname.startsWith(n.to));
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-secondary"
                }`}>
                <Icon className="w-4 h-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="overline">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          <Button variant="ghost" className="w-full justify-start gap-2 mb-1" data-testid="change-password-btn"
            onClick={() => setPwOpen(true)}>
            <KeyRound className="w-4 h-4" /> Mot de passe
          </Button>
          <Button variant="outline" className="w-full justify-start gap-2" data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}>
            <LogOut className="w-4 h-4" /> Déconnexion
          </Button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-w-0">
        {children}
        <Copyright />
      </main>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Changer mon mot de passe</DialogTitle>
            <DialogDescription>Le nouveau mot de passe remplace celui reçu à la création du compte.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Mot de passe actuel</Label>
              <Input type="password" className="mt-1" value={pw.current_password} data-testid="pw-current"
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
            <div><Label className="text-xs">Nouveau mot de passe (8 caractères minimum)</Label>
              <Input type="password" className="mt-1" value={pw.new_password} data-testid="pw-new"
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
            <div><Label className="text-xs">Confirmer</Label>
              <Input type="password" className="mt-1" value={pw.confirm} data-testid="pw-confirm"
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></div>
            <Button className="w-full" onClick={changePassword} data-testid="pw-submit">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, icon: Icon }) {
  return (
    <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border px-8 py-5 flex items-center justify-between">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-6 h-6 text-slate-400" />}
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}
